/**
 * @internal
 * Internal styled components and styles for the TimePicker component.
 *
 * DO NOT import this file directly from outside the TimePicker component directory.
 * Use the public API exported from the component's index.tsx instead.
 * These internal implementation details may change without notice.
 */

import { styled } from '@withjoy/joykit';
import { anatomy } from '@shared/joykit/packages/core/common/utils/anatomy';
import { createMultiPartStylesContext, MultiPartStyleConfig, PartsStyleObject } from '@shared/joykit/packages/core/common/utils/styleConfig';
import { StyleSystemProps, Box } from '../Box';
import type { TimePickerProps } from './TimePicker.types';
import { animationTransition } from '@shared/utils/animationTransition';

/**
 * @internal
 * Root styled component for TimePicker layout.
 */
export const TimePickerRoot = styled(Box)``;

/**
 * @internal
 * Wrapper styled component for the input field.
 */
export const TimePickerInputWrapper = styled(Box)``;

/**
 * @internal
 * Styled wrapper for the native time input on mobile.
 * Includes webkit resets to remove browser chrome that affects height.
 * The native input is made transparent and overlays InputV2 to capture taps.
 */
export const NativeTimeInputOverlay = styled(Box)`
  /* Reset browser-specific time input styling */
  input[type='time'] {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: textfield;
    background: transparent;
    border: none;
    color: transparent;

    /* Hide the native picker indicator and spinners */
    &::-webkit-calendar-picker-indicator {
      background: transparent;
      cursor: pointer;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-clear-button {
      display: none;
      -webkit-appearance: none;
    }

    /* Ensure consistent sizing */
    &::-webkit-datetime-edit,
    &::-webkit-datetime-edit-fields-wrapper,
    &::-webkit-datetime-edit-text,
    &::-webkit-datetime-edit-hour-field,
    &::-webkit-datetime-edit-minute-field,
    &::-webkit-datetime-edit-ampm-field {
      display: none;
    }
  }
`;
/**
 * @internal
 * Styled component for the options list with custom scrollbar styling.
 * Uses styled-components because pseudo-selectors aren't supported in StyleSystemProps.
 */
export const TimePickerOptionsList = styled(Box)`
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.scrollbarHandle};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.mono8};
  }
`;
/**
 * @internal
 * Styled component for individual time option buttons.
 */
export const TimePickerOption = styled(Box)``;

/**
 * @internal
 * Fixed height for the time options list.
 */
export const TIME_LIST_HEIGHT = 280;

/**
 * @internal
 * Error message auto-dismiss timeout in milliseconds.
 */
export const ERROR_TIMEOUT_MS = 5000;

/**
 * @internal
 * Dropdown shadow matching other JoyKit popover components.
 */
export const DROPDOWN_SHADOW = '0px 7px 27px rgba(44, 41, 37, 0.06), 0px 4px 16px rgba(0, 0, 0, 0.07)';

// Base styles for the root container
const baseStyleRoot: StyleSystemProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  position: 'relative',
};

// Base styles for the input wrapper
const baseStyleInputWrapper: StyleSystemProps = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

// Base styles for the options list (scrollable dropdown)
// Note: Custom scrollbar styling is applied via styled-components on TimePickerOptionsList
// No horizontal padding to allow full-width option rows matching selector pattern
const baseStyleOptionsList: StyleSystemProps = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: `${TIME_LIST_HEIGHT}px`,
  overflowY: 'auto',
  paddingX: 0,
  paddingY: 3,
};

// Base styles for individual time option
// Uses mono colorscheme to match form selector pattern
// Note: No transition on background-color to prevent visual lag during arrow key navigation
const baseStyleOption: StyleSystemProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '48px', // Match Menu component touch target
  paddingX: 5,
  paddingY: 3,
  borderRadius: 0, // No radius to match selector pattern
  cursor: 'pointer',
  transition: animationTransition('color'),
  fontSize: '500',
  color: 'mono14',
  backgroundColor: 'transparent',
  width: '100%',
  textAlign: 'left',
  _hover: {
    backgroundColor: 'mono2',
    color: 'mono14',
  },
  _focus: {
    outline: 0,
  },
};

/**
 * @internal
 * Selected option styles using mono colorscheme to match form selector pattern.
 */
export const getSelectedOptionStyles = (): StyleSystemProps => ({
  backgroundColor: 'mono3',
  color: 'mono14',
  fontWeight: 'medium',
  _hover: {
    backgroundColor: 'mono3',
    color: 'mono14',
  },
});

// Anatomy for style config
const timePickerAnatomy = anatomy('TimePicker').parts('root', 'inputWrapper', 'optionsList', 'option');

// Base style object
const timePickerBaseStyle: PartsStyleObject<typeof timePickerAnatomy> = {
  root: baseStyleRoot,
  inputWrapper: baseStyleInputWrapper,
  optionsList: baseStyleOptionsList,
  option: baseStyleOption,
};

/**
 * @internal
 * Multi-part style config for the TimePicker component.
 */
export const timePickerStyles: MultiPartStyleConfig<TimePickerProps, typeof timePickerAnatomy> = {
  parts: timePickerAnatomy.keys,
  baseStyle: timePickerBaseStyle,
};

// Context provider for styles (hook is available internally but not exported as it's unused)
const [TimePickerStylesProvider, _useTimePickerStyles] = createMultiPartStylesContext(timePickerAnatomy);

/**
 * @internal
 * Context provider for TimePicker styles.
 */
export { TimePickerStylesProvider };

/**
 * @internal
 * Icon button styles for the input enhancers (clear and clock buttons).
 * Uses semantic color tokens for consistency with other JoyKit components.
 */
export const iconButtonStyles: StyleSystemProps = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 2,
  borderRadius: 2,
  cursor: 'pointer',
  backgroundColor: 'transparent',
  transition: animationTransition('color', 'background-color'),
  _hover: {
    backgroundColor: 'fillLightActive',
  },
  _active: {
    backgroundColor: 'mono3',
  },
  _disabled: {
    cursor: 'not-allowed',
    color: 'buttonDisabledText',
    _hover: {
      backgroundColor: 'transparent',
      color: 'buttonDisabledText',
    },
  },
};

/**
 * @internal
 * Error message styles using semantic tokens.
 */
export const errorMessageStyles: StyleSystemProps = {
  fontSize: '400',
  color: 'inputBorderError',
  paddingTop: 2,
  paddingLeft: 1,
};

/**
 * @internal
 * Native time input overlay placeholder styles.
 */
export const nativePlaceholderStyles: StyleSystemProps = {
  position: 'absolute',
  left: 5,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'inputPlaceholder',
  pointerEvents: 'none',
  fontSize: '500',
};

/**
 * @internal
 * Get styles for the native time input element.
 * Used in both mobile and pre-hydration renders.
 * @deprecated Use the overlay approach with InputV2 + transparent native input instead.
 */
export const getNativeInputStyles = (isInvalid: boolean, isDisabled: boolean, includeFocusStates = true): StyleSystemProps => ({
  width: '100%',
  height: 9,
  paddingX: 5,
  paddingY: 4,
  fontSize: '500',
  borderRadius: 2,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: isInvalid ? 'inputBorderError' : 'inputBorder',
  backgroundColor: isDisabled ? 'inputFillDisabled' : 'inputFill',
  color: isDisabled ? 'inputTextDisabled' : 'inputText',
  cursor: isDisabled ? 'not-allowed' : 'text',
  ...(includeFocusStates && {
    _focus: {
      outline: 0,
      borderColor: 'inputBorderFocused',
    },
    _disabled: {
      cursor: 'not-allowed',
    },
  }),
});

/**
 * @internal
 * Styles for the invisible native time input that overlays InputV2 on mobile.
 * This input captures taps to trigger the native picker while InputV2 handles the visual.
 */
export const nativeInputOverlayStyles: StyleSystemProps = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  cursor: 'pointer',
  zIndex: 1,
};
